/**
 * =====================================================
 * ZENTRALE API-KONFIGURATION
 * =====================================================
 * Verhindert hardcodierte localhost-URLs im gesamten Projekt
 * 
 * Usage:
 *   import { getApiUrl } from '../utils/apiConfig';
 *   axios.get(getApiUrl('/api/users'))
 * =====================================================
 */

/**
 * Gibt die korrekte API Base URL zurück
 * - Production: Same-Origin (Vercel Rewrite zu Railway)
 * - Development: localhost:5001 oder REACT_APP_API_URL
 * @returns {string} Base URL (leer in Production)
 */
export const resolveApiBaseUrl = ({ hostname = '', explicit = '', nodeEnv = '' } = {}) => {
  const isCustomDomain = hostname === 'immo-automation.de' || hostname === 'www.immo-automation.de';
  const isVercelDeployment = hostname.includes('vercel.app') ||
    hostname.includes('vercel.dev') ||
    hostname.includes('now.sh');

  if (isCustomDomain || isVercelDeployment) {
    return '';
  }

  if (explicit) {
    return String(explicit).replace(/\/$/, '');
  }

  return nodeEnv === 'production' ? '' : 'http://localhost:5001';
};

export const getApiBaseUrl = () => {
  const explicit = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.__API_BASE_URL__ : '');
  return resolveApiBaseUrl({
    hostname: typeof window !== 'undefined' ? window.location.hostname || '' : '',
    explicit,
    nodeEnv: process.env.NODE_ENV,
  });
};

/**
 * Erstellt eine vollständige API URL
 * @param {string} path - API-Pfad (z.B. '/api/users' oder 'api/users')
 * @returns {string} Vollständige URL
 * @example
 *   getApiUrl('/api/users') // => '' + '/api/users' (Production)
 *   getApiUrl('api/users')  // => 'http://localhost:5001/api/users' (Dev)
 */
export const getApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Gibt die Upload-URL zurück für Datei-Downloads
 * @param {string} filename - Dateiname (z.B. 'beleg_123.pdf')
 * @returns {string} Vollständige Upload URL
 * @example
 *   getUploadUrl('beleg.pdf') // => '/uploads/beleg.pdf' (Production)
 */
export const getUploadUrl = (filename) => {
  return getApiUrl(`/uploads/${filename}`);
};

/**
 * Prüft ob App in Production läuft
 * @returns {boolean} true wenn Production
 */
export const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Erstellt vollständige API URL (Alias für getApiUrl)
 * Nützlich für Lesbarkeit in manchen Kontexten
 */
export const buildApiUrl = getApiUrl;

// Export als Default für einfachen Import
const apiConfig = {
  getApiBaseUrl,
  getApiUrl,
  buildApiUrl,
  getUploadUrl,
  isProduction,
};

export default apiConfig;
